import { isFulfilled } from "@reduxjs/toolkit";
import React, { FC, memo, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import DefaultCentreImg from "../../assets/img/Buildings.svg";
import Logo from "../../assets/img/Logo.svg";
import DefaultPerson from "../../assets/img/default_person.png";
import Logtimesheet from "../../components/Candidate/Timesheet/Logtimesheet";
import ConfirmBlock from "../../components/Client/Vacancies/Popup/ConfirmBlock";
import PendingTimesheetApproval from "../../components/Client/Vacancies/Popup/PendingTimesheetApproval";
import RatingCandidate from "../../components/Client/Vacancies/Popup/RatingCandidate";
import EditBankAccount from "../../components/OnboardingSteps/CompletedProfile/edit-forms/EditBankAccount";
import AccountSettings from "../../components/UI/Icons/AccountSettings";
import Admin from "../../components/UI/Icons/Admin";
import CMother from "../../components/UI/Icons/CMother";
import Candidates from "../../components/UI/Icons/Candidates";
import Centres from "../../components/UI/Icons/Centres";
import Chevron from "../../components/UI/Icons/Chevron";
import CloseMenu from "../../components/UI/Icons/CloseMenu";
import Dashboard from "../../components/UI/Icons/Dashboard";
import Jobs from "../../components/UI/Icons/Jobs";
import LogOut from "../../components/UI/Icons/LogOut";
import MyProfile from "../../components/UI/Icons/MyProfile";
import Referral from "../../components/UI/Icons/Referral";
import Search from "../../components/UI/Icons/Search";
import Timesheet from "../../components/UI/Icons/Timesheet";
import VerticalLine from "../../components/UI/Icons/VerticalLine";
import PopupModal from "../../components/UI/Popup";
import useViewportHeight from "../../components/UI/useViewportHeight";
import { STORAGE_KEYS } from "../../constants/storageKeys";
import Roles from "../../models/roles";
import { AUTH_ROUTE, NON_AUTH_ROUTE } from "../../pages/App/routes";
import {
    useAppDispatch as useDispatch,
    useAppSelector as useSelector,
} from "../../store";
import {
    hideTransparentPreloaderAction,
    showTransparentPreloaderAction,
} from "../../store/application/slice";
import { setShowNotVerifiedErrorAction } from "../../store/auth/slice";
import { logOutThunk } from "../../store/auth/thunks";
import { getAllCenters } from "../../store/centers/thunks";
import { getDateParamsForAPI, getDefaultDateRange } from "../../store/timesheet/parser";
import { getClientTimesheetPendingApprovalsThunk } from "../../store/timesheet/thunks";
import {
    resetSelectedCentreDetails,
    setSelectedCentreDetailsAction,
} from "../../store/vacancy/slice";
import { getCandidatesForRatingThunk } from "../../store/vacancy/thunks";
import storage from "../../utils/storage";
import { capitalizeName, truncateString } from "../../utils/stringUtils";

/**
 * Portal Page Props Interface
 * @interface PortalPageProps
 */

interface PortalPageProps {
  /** pass the children pages in the layout */
  children: React.ReactNode;
  setLayoutReady: (ready: boolean) => void;
}

/**
 * MenuLink helper props
 */
interface MenuLinkProps {
  to: string | undefined;
  active?: boolean;
  onClick?: () => void;
  icon?: React.FC<any> | any;
  label: string;
  extraChildren?: React.ReactNode;
  exact?: boolean;
  href?: string;
}

const MenuLink: React.FC<MenuLinkProps> = ({
  to,
  active,
  onClick,
  icon: Icon,
  label,
  extraChildren,
}) => {
  // When `to` is undefined (rare) use '#' as fallback
  return (
    <Link
      to={to ?? "#"}
      className={`menu-link ${active ? "active" : ""}`}
      onClick={onClick}
      data-tooltip={label} // tooltip text used when sidebar is collapsed
      aria-label={label}
      tabIndex={0}
    >
      {Icon && <Icon />}
      <span className="menu-label">{label}</span>
      {extraChildren}
    </Link>
  );
};

const Portal: FC<PortalPageProps> = ({ children, setLayoutReady }) => {
  useViewportHeight();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const user = useSelector((state) => state.auth.user);
  const nonRatedCandidates = useSelector(
    (state) => state.vacancy.nonRatedCandidates
  );
  const profile = useSelector((state) => state.onboarding.personaldetails);
  const allCentersList = useSelector((state) => state.Centres.allCenters);
  const selectedCentreDetailsUuid = useSelector(
    (state) => state.vacancy.selectedCentreDetails
  );
  const { totalPendingPayrollTimesheet } = useSelector(
    (state) => state.timesheet
  );
  const stepCompletion = useSelector(
    (state) => state.onboarding.completedstatus
  );

  const indexPage =
    user && user.role.name === Roles.CANDIDATE
      ? "Jobs"
      : user &&
        (user.role.name === Roles.SUPER_ADMIN ||
          user?.role.name === Roles.GENERAL_ADMIN)
      ? "Dashboard"
      : "";

  const [openDropdown, setOpenDropdown] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [pageName, setPageName] = useState<string | null | any>(
    indexPage || null
  );
  const [selectedCentreDetails, setSelectedCentreDetails] = useState<
    any | null
  >(null);
  const [showRatingPopup, setShowRatingPopup] = useState(false);
  const [showConfirmBlockPopup, setShowConfirmBlockPopup] = useState(false);
  const [blockList, setBlockList] = useState([]);
  const [isShowPendingTimesheetNotification, setIsShowPendingTimesheetNotification] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null] | null>(
    getDefaultDateRange(30)
  );
  const [showBankAccountPopup, setShowBankAccountPopup] = useState(false);
  const [showLogTimesheet, setShowLogTimesheet] = useState(false);

  const noCentresDropdownNeeded = ["My Centres & Users", "Timesheet"];

  const profilePicture =
    user !== null && user?.photo !== null ? user?.photo : "";

  const centreIdFromLocal = storage.get(STORAGE_KEYS.SELECTED_CENTRE_ID);
  const [isEditVacancy, setIsEditVacancy] = useState(false);
  const candidatePendingTimesheetList = useSelector(
    (state) => state.timesheet.candidateLogTimeSheet
  );
  const clientPendingTimesheetList = useSelector(
    (state) => state.timesheet.clientPendingTimesheetList
  );
  const totalApprovalTimesheet = useSelector(
    (state) => state.timesheet.totalClientPendingTimesheetApproval
  );

  useEffect(() => {
    const getCentreId = storage.get(STORAGE_KEYS.SELECTED_CENTRE_ID);
    if (allCentersList.length === 1) {
      dispatch(setSelectedCentreDetailsAction(allCentersList[0]));
    } else {
      const getCentreData = allCentersList.find(
        (allCentersData) => allCentersData.id === getCentreId
      );
      setSelectedCentreDetails(getCentreData);
      setOpenDropdown(false);
    }  
  }, [dispatch, allCentersList, selectedCentreDetailsUuid, centreIdFromLocal]);

  useEffect(() => {
    if (!location.pathname.includes("id-verification")) {
      dispatch(setShowNotVerifiedErrorAction(!true));
    }
  }, [dispatch, location]);

  const handleLogOut = async () => {
    dispatch(showTransparentPreloaderAction());
    const result = await dispatch(logOutThunk());
    if (isFulfilled(result)) {
      dispatch(hideTransparentPreloaderAction());
      navigate(NON_AUTH_ROUTE.LOGIN);
    }
  };

  const handlePageName = (page: string) => {
    setIsOpen(false);
    switch (page) {
      case "jobs":
        setPageName("Jobs");
        break;
      case "centres":
        setPageName("Centres");
        break;
      case "my_profile":
        setPageName("My Profile");
        break;
      case "timesheet":
        setPageName("Timesheet");
        break;
      case "account_settings":
        setPageName("Account Settings");
        break;
      case "dashboard":
        setPageName("Dashboard");
        break;
      case "employees":
        setPageName("Employees");
        break;
      case "candidates":
        setPageName("Candidates");
        break;
      case "admin_centers":
        setPageName("Centres & Users");
        break;
      case "referral":
        setPageName("Referral Tracker");
        break;
      case "users":
        setPageName("Admin");
        break;
      case "client_centers":
        setPageName("My Centres & Users");
        break;
      case "vacancies":
        setPageName("Vacancies");
        break;
    }
  };

  const isAdmin = useMemo(
    () =>
      user?.role.name === Roles.SUPER_ADMIN ||
      user?.role.name === Roles.GENERAL_ADMIN,
    [user]
  );
  const isCentreUsers = useMemo(
    () =>
      user?.role.name === Roles.CENTER_ADMIN ||
      user?.role.name === Roles.CENTER_USER,
    [user]
  );
  const isCandidate = useMemo(
    () => user?.role.name === Roles.CANDIDATE,
    [user]
  );

  useEffect(() => {
    if (location.pathname.includes("dashboard")) {
      setPageName("Dashboard");
    } else if (location.pathname.includes("jobs")) {
      setPageName("Jobs");
    } else if (location.pathname.includes("employees")) {
      setPageName("Employees");
    } else if (location.pathname.includes("candidates")) {
      setPageName("Candidates");
    } else if (location.pathname.includes("centres") && isAdmin) {
      setPageName("Centres & Users");
    } else if (location.pathname.includes("centres") && isCentreUsers) {
      setPageName("My Centres & Users");
    } else if (location.pathname.includes("centres") && isCandidate) {
      setPageName("Centres");
    } else if (location.pathname.includes("my-profile")) {
      setPageName("My Profile");
    } else if (location.pathname.includes("timesheet")) {
      setPageName("Timesheet");
    } else if (location.pathname.includes("referral")) {
      setPageName("Referral Tracker");
    } else if (location.pathname.includes("settings")) {
      setPageName("Account Settings");
    } else if (location.pathname.includes("users")) {
      setPageName("Admin");
    } else if (
      location.pathname.includes("vacancies") ||
      location.pathname.includes("vacancy")
    ) {
      setPageName("Vacancies");
    }
  }, [location, isAdmin, isCandidate, isCentreUsers]);

  const handlCentrePageRedirection = () => {
    isCentreUsers &&
      pageName !== "Vacancies" &&
      navigate(AUTH_ROUTE.CLIENT_CENTRES);
  };

  const resetSelectedCentreFilter = () => {
    dispatch(resetSelectedCentreDetails());
  };

  const generateUser = (role: Roles) => {
    switch (role) {
      case Roles.SUPER_ADMIN:
        return Roles.SUPER_ADMIN;
      case Roles.GENERAL_ADMIN:
        return Roles.GENERAL_ADMIN;
      case Roles.CENTER_ADMIN:
        return Roles.CENTER_ADMIN;
      case Roles.CENTER_USER:
        return Roles.CENTER_USER;
      default:
        return Roles.CANDIDATE;
    }
  };

  useEffect(() => {
    dispatch(showTransparentPreloaderAction());
    const loadData = async () => {
      try {
        if (isCentreUsers || isAdmin) {
          await dispatch(getAllCenters());
        }

        if(isAdmin) {
          const { start_date, end_date } = getDateParamsForAPI(dateRange);

          // await dispatch(
          //   getPayrolPendingTimesheetThunk({
          //     searchValue: '',
          //     page: 1,
          //     per_page: 10,
          //     sort_by: "first_name",
          //     sort_order: "asc",
          //     start_date,
          //     end_date,
          //     centres: [],
          //     candidates: [],
          //     type: "dashboard"
          //   })
          // );
        }

        if (isCentreUsers) {
          await dispatch(getClientTimesheetPendingApprovalsThunk());
          await dispatch(getCandidatesForRatingThunk());
        }
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLayoutReady(true);
        dispatch(hideTransparentPreloaderAction());
      }
    };

    loadData();
  }, [dispatch, isCentreUsers, isAdmin, isCandidate, setLayoutReady, dateRange]);

  useEffect(() => {
    if(totalApprovalTimesheet > 0) {
      setIsShowPendingTimesheetNotification(true)
    } else {
      setIsShowPendingTimesheetNotification(false)
    }
  }, [totalApprovalTimesheet]);

  useEffect(() => {
    if (clientPendingTimesheetList.length > 0) {
      setShowRatingPopup(false);
      setIsShowPendingTimesheetNotification(true)
    } else if (nonRatedCandidates.length > 0) {
      setShowRatingPopup(true);
    } else {
      setIsShowPendingTimesheetNotification(false)
    }
  }, [clientPendingTimesheetList, nonRatedCandidates]);

  const [searchTerm, setSearchTerm] = useState("");
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value.toLowerCase());
  };

  const handleRedirectCentre = async (centreDetails: any) => {
    if (centreDetails?.id !== "") {
      localStorage.setItem("centre_id", String(centreDetails?.id));
      if (
        (isCentreUsers || isAdmin) &&
        (pageName === "Vacancies" || pageName === "Candidates")
      ) {
        setOpenDropdown(false);
        setSelectedCentreDetails(centreDetails);
        await dispatch(setSelectedCentreDetailsAction(centreDetails));
      } else {
        navigate(AUTH_ROUTE.CLIENT_CENTRES);
      }
    }
    setSearchTerm("");
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdown(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (location.pathname.includes("client/centres")) {
      resetSelectedCentreFilter();
    }
    if (location.pathname.includes("edit-vacancy")) {
      setIsEditVacancy(true);
    } else {
      setIsEditVacancy(false);
    }
  }, [location]);

  const handleClose = () => {
    setShowRatingPopup(false);
  };

  // 1. Trigger bank account popup first if needed
  useEffect(() => {
    if (isCandidate && stepCompletion?.bankAccount !== 1 && profile?.verification_status === "verified" && Object.values(candidatePendingTimesheetList).length > 0) {
      setShowBankAccountPopup(true);
    } else if (isCandidate && Object.values(candidatePendingTimesheetList).length > 0) {
      setShowLogTimesheet(true);
    }
  }, [isCandidate, stepCompletion, candidatePendingTimesheetList, profile?.verification_status]);

  // 2. Handle bank account popup close and trigger timesheet
  const handleCloseBankAccPopup = () => {
    setShowBankAccountPopup(false);

    // After closing, check if timesheet needs to be shown
    if (isCandidate && Object.values(candidatePendingTimesheetList).length > 0) {
      setShowLogTimesheet(true);
    }
  };

  const showBlockConfirmationModal = (candidatesToBlock: any) => {
    setBlockList(candidatesToBlock);
    setShowConfirmBlockPopup(true);
  };

  const hideBlockConfirmationModal = () => {
    setShowConfirmBlockPopup(false);
  };

  const handleMobileDropDown = () => {
    !noCentresDropdownNeeded.includes(pageName) &&
      setOpenDropdown((prev) => !prev); // Toggle correctly
  };

  // sidebar class: if collapsed add 'collapsed' class (used by tooltip CSS)
  const sidebarClass = `sidebar-menu flex-column justify-content-between ${
    isOpen ? "menu-open" : "collapsed"
  }`;

  return (
    <>
      <div className={sidebarClass}>
        <div className="d-flex flex-column">
          <Link
            to="#"
            onClick={() => setIsOpen(!isOpen)}
            className="menu-toggle"
          >
            {isOpen ? <CMother /> : <CloseMenu />}
            <span className="text-teal-dark">Close Menu</span>
          </Link>

          {user && user.role.name === Roles.CANDIDATE ? (
            <>
              <MenuLink
                to={AUTH_ROUTE.JOBS}
                active={pageName === "Jobs"}
                onClick={() => handlePageName("jobs")}
                icon={Jobs}
                label="Jobs"
              />
              <MenuLink
                to={AUTH_ROUTE.CENTRES}
                active={pageName === "Centres"}
                onClick={() => handlePageName("centres")}
                icon={Centres}
                label="Centres"
              />
              <MenuLink
                to={
                  profile !== null && profile?.verification_status !== "pending"
                    ? AUTH_ROUTE.MYPROFILE_COMPLETED
                    : AUTH_ROUTE.MY_PROFILE
                }
                active={pageName === "My Profile"}
                onClick={() => handlePageName("my_profile")}
                icon={MyProfile}
                label="My Profile"
              />
              <MenuLink
                to={AUTH_ROUTE.TIMESHEET}
                active={pageName === "Timesheet"}
                onClick={() => handlePageName("timesheet")}
                icon={Timesheet}
                label="Timesheet"
                // extraChildren={<div className="menu-alert"></div>}
              />
            </>
          ) : null}

          {user &&
          (user.role.name === Roles.SUPER_ADMIN ||
            user.role.name === Roles.GENERAL_ADMIN) ? (
            <>
              <MenuLink
                to={AUTH_ROUTE.ADMIN_DASHBOARD}
                active={pageName === "Dashboard"}
                onClick={() => handlePageName("dashboard")}
                icon={Dashboard}
                label="Dashboard"
              />
              <MenuLink
                to={AUTH_ROUTE.ADMIN_URGENT_VACANCIES}
                active={pageName === "Vacancies"}
                onClick={() => handlePageName("vacancies")}
                icon={Jobs}
                label="Vacancies"
              />
              <MenuLink
                to={AUTH_ROUTE.ADMIN_CANDIDATES}
                active={pageName === "Employees"}
                onClick={() => handlePageName("employees")}
                icon={Candidates}
                label="Employees"
              />
              <MenuLink
                to={AUTH_ROUTE.ADMIN_CENTERS}
                active={pageName === "Centres & Users"}
                onClick={() => handlePageName("admin_centers")}
                icon={Centres}
                label="Centres & Users"
              />
              <MenuLink
                to={AUTH_ROUTE.ADMIN_TIMESHEET_PAYROLL_PENDING}
                active={pageName === "Timesheet"}
                onClick={() => handlePageName("timesheet")}
                icon={Timesheet}
                label="Timesheet"
                extraChildren={ totalPendingPayrollTimesheet > 0 ? <div className="menu-alert"></div> : "" }
              />
              <MenuLink
                to={AUTH_ROUTE.ADMIN_REFERRAL}
                active={pageName === "Referral Tracker"}
                onClick={() => handlePageName("referral")}
                icon={Referral}
                label="Referral Tracker"
              />
            </>
          ) : null}

          {user &&
          (user.role.name === Roles.CENTER_ADMIN ||
            user.role.name === Roles.CENTER_USER) ? (
            <>
              <MenuLink
                to={AUTH_ROUTE.CLIENT_VACANCIES}
                active={pageName === "Vacancies"}
                onClick={() => handlePageName("vacancies")}
                icon={Jobs}
                label="Vacancies"
              />
              <MenuLink
                to={AUTH_ROUTE.CLIENT_CANDIDATES}
                active={pageName === "Candidates"}
                onClick={() => handlePageName("candidates")}
                icon={Candidates}
                label="Candidates"
              />
              <MenuLink
                to={AUTH_ROUTE.CLIENT_CENTRES}
                active={pageName === "My Centres & Users"}
                onClick={() => handlePageName("client_centers")}
                icon={Centres}
                label="My Centres & Users"
              />
              {user.timesheet_access === 1 ? (
                <MenuLink
                  to={AUTH_ROUTE.CLIENT_TIMESHEET_APPROVAL}
                  active={pageName === "Timesheet"}
                  onClick={() => handlePageName("timesheet")}
                  icon={Timesheet}
                  label="Timesheet"
                  extraChildren={ isShowPendingTimesheetNotification ? <div className="menu-alert"></div> : ""}
                />
              ) : (
                ""
              )}
            </>
          ) : null}
        </div>

        <div className="d-flex flex-column">
          {user && user.role.name === Roles.CANDIDATE ? (
            <MenuLink
              to={AUTH_ROUTE.SETTINGS}
              active={pageName === "Account Settings"}
              onClick={() => handlePageName("account_settings")}
              icon={AccountSettings}
              label="Account Settings"
            />
          ) : null}

          {user &&
          (user.role.name === Roles.CENTER_ADMIN ||
            user.role.name === Roles.CENTER_USER) ? (
            <MenuLink
              to={AUTH_ROUTE.CLIENT_SETTINGS}
              active={pageName === "Account Settings"}
              onClick={() => handlePageName("account_settings")}
              icon={AccountSettings}
              label="Account Settings"
            />
          ) : null}

          {user &&
          (user.role.name === Roles.SUPER_ADMIN ||
            user.role.name === Roles.GENERAL_ADMIN) ? (
            <>
              <MenuLink
                to={AUTH_ROUTE.ADMIN_USERS}
                active={pageName === "Admin"}
                onClick={() => handlePageName("users")}
                icon={Admin}
                label="Admin"
              />
              {/* preserved code for potential admin settings */}
            </>
          ) : null}

          <Link
            to="#"
            onClick={handleLogOut}
            className="menu-link"
            data-tooltip="Logout"
            aria-label="Logout"
            tabIndex={0}
          >
            <LogOut />
            <span className="menu-label">Logout</span>
          </Link>
        </div>
      </div>

      {/* Page content */}
      <div className="page-content d-flex flex-column gap-25">
        <div className="main-header d-flex gap-30 gap-mobile-20 align-items-center justify-content-between">
          <div className="d-flex gap-30 gap-mobile-20 align-items-center">
            <svg
              className="d-md-none min-w-56px"
              onClick={() => setIsOpen(!isOpen)}
              width="56"
              height="58"
              viewBox="0 0 56 58"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="56" height="57.3793" fill="#51C2B9" />
              <path
                d="M19 33C19 33.5523 19.4477 34 20 34H36C36.5523 34 37 33.5523 37 33C37 32.4477 36.5523 32 36 32H20C19.4477 32 19 32.4477 19 33ZM19 28C19 28.5523 19.4477 29 20 29H36C36.5523 29 37 28.5523 37 28C37 27.4477 36.5523 27 36 27H20C19.4477 27 19 27.4477 19 28ZM20 22C19.4477 22 19 22.4477 19 23C19 23.5523 19.4477 24 20 24H36C36.5523 24 37 23.5523 37 23C37 22.4477 36.5523 22 36 22H20C19.4477 22 19 22.4477 19 23C19 23.5523 19.4477 24 20 24H36C36.5523 24 37 23.5523 37 23C37 22.4477 36.5523 22 36 22H20C19.4477 22 19 22.4477 19 23Z"
                fill="#167E76"
              />
            </svg>
            <img
              src={Logo}
              alt="Logo"
              width={120}
              height={36}
              className="img-fluid header-logo"
            />
            <VerticalLine />
            <div className="fs-22 fs-mobile-16 fw-400 text-grey-medium">
              {pageName ? pageName : "Jobs"}
            </div>
          </div>

          <div className="d-flex gap-40 gap-mobile-10">
            <div
              className={`${
                (isAdmin && pageName === "Vacancies") ||
                (isCentreUsers &&
                  (pageName === "Vacancies" || pageName === "Candidates"))
                  ? "d-none d-lg-flex"
                  : "d-flex"
              } gap-15 align-items-center`}
            >
              <div className="w-44px h-44px border-solid border-2 border-grey-dark radius-40px">
                <img
                  src={profilePicture && !isAdmin ? profilePicture : DefaultPerson}
                  alt="profile-picture"
                  width={40}
                  height={40}
                  className="rounded-circle object-fit-cover"
                />
              </div>
              <div className="d-none d-lg-flex flex-column gap-4">
                <div
                  className="fs-22 fw-400 text-black lh-1"
                  title={`${capitalizeName(user?.first_name ?? "")} ${capitalizeName(user?.last_name ?? "")}`}
                >
                  {truncateString(
                    capitalizeName(user?.first_name ?? "") + " " + capitalizeName(user?.last_name ?? ""),
                    20
                  )}
                </div>
                <div className="fs-13 fw-400 text-grey-dark">
                  {generateUser(
                    user ? (user?.role?.name as Roles) : Roles.CANDIDATE
                  )}
                </div>
              </div>
            </div>

            {(isCentreUsers && pageName !== "Account Settings") ||
            (isAdmin && pageName === "Vacancies") ? (
              <>
                {allCentersList?.length > 1 ? (
                  <div
                    className="d-flex gap-15 align-items-center position-relative"
                    onClick={handleMobileDropDown}
                  >
                    {selectedCentreDetails ? (
                      <>
                        <div className="position-relative">
                          <div
                            className={`w-44px h-44px border-solid border-2 border-grey-dark radius-40px position-relative z-1 ${
                              selectedCentreDetails &&
                              selectedCentreDetails?.photo !== null
                                ? ""
                                : "bg-white d-flex align-items-center justify-content-center"
                            }`}
                          >
                            <img
                              src={
                                selectedCentreDetails?.photo
                                  ? selectedCentreDetails?.photo
                                  : DefaultCentreImg
                              }
                              alt="profile-picture"
                              width={`${
                                selectedCentreDetails?.photo !== null
                                  ? "40"
                                  : "24"
                              }`}
                              height={`${
                                selectedCentreDetails?.photo !== null
                                  ? "40"
                                  : "24"
                              }`}
                              className="rounded-circle object-fit-cover"
                            />
                          </div>
                        </div>
                        <div className="d-none d-lg-flex flex-column gap-4 cursor-pointer">
                          <div className="fs-22 fw-400 text-black lh-1">
                            {truncateString(
                              selectedCentreDetails.business_name,
                              30
                            )}
                          </div>
                          <div className="max-w-250px">
                            <div className="fs-13 fw-400 text-grey-dark">
                              {selectedCentreDetails.address}
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="position-relative">
                          <div
                            className={`w-44px h-44px border-solid border-2 border-grey-dark radius-40px position-relative z-3 ${
                              allCentersList && allCentersList[0].photo !== null
                                ? ""
                                : "bg-white d-flex align-items-center justify-content-center"
                            }`}
                          >
                            <img
                              src={
                                allCentersList && allCentersList[0].photo
                                  ? allCentersList[0].photo
                                  : DefaultCentreImg
                              }
                              alt="profile-picture"
                              width={`${
                                allCentersList &&
                                allCentersList[0].photo !== null
                                  ? "40"
                                  : "24"
                              }`}
                              height={`${
                                allCentersList &&
                                allCentersList[0].photo !== null
                                  ? "40"
                                  : "24"
                              }`}
                              className="rounded-circle object-fit-cover"
                              onClick={() => handlCentrePageRedirection()}
                            />
                          </div>
                          <div className="w-44px h-44px border-solid border-2 border-grey-dark radius-40px bg-teal-light position-absolute top-0 left-5px z-2"></div>
                          <div className="w-44px h-44px border-solid border-2 border-grey-dark radius-40px bg-teal position-absolute top-0 left-10px z-1"></div>
                        </div>
                        <div className="d-none d-lg-flex flex-column gap-4 cursor-pointer max-w-150px">
                          <div className="fs-22 fw-400 text-black lh-1 text-truncate">
                            {"All Centres"}
                          </div>
                          <div className="fs-13 fw-400 text-grey-dark text-truncate">
                            Viewing {allCentersList?.length} total
                          </div>
                        </div>
                      </>
                    )}
                    {!isEditVacancy && (
                      <div
                        className="d-none d-lg-block cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenDropdown((prev) => !prev);
                        }}
                      >
                        {!noCentresDropdownNeeded.includes(pageName) ? (
                          <Chevron
                            isOpen={openDropdown}
                            fill={`${!openDropdown ? "#AAAAAA" : "black"}`}
                          />
                        ) : (
                          ""
                        )}
                      </div>
                    )}

                    {openDropdown && allCentersList?.length > 1 && (
                      <div
                        className="position-absolute end-0 d-flex flex-column dropdown-menu top-56px w-266px flex-wrap"
                        ref={dropdownRef}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {!location.pathname.includes("client/candidates") ? (
                          <div className="dropdown-item justify-content-center cursor-pointer">
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z"
                                fill="black"
                              />
                            </svg>
                            <div
                              className="fs-16 fw-400 text-black"
                              onClick={resetSelectedCentreFilter}
                            >
                              View all {allCentersList?.length} centres
                            </div>
                          </div>
                        ) : null}
                        <div className="dropdown-item p-6px">
                          <div className="form-group w-100 input-icon icon-left">
                            <Search />
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search..."
                              onChange={handleSearch}
                              value={searchTerm}
                            />
                          </div>
                        </div>
                        <div className="border-top border-black overflow-auto max-h-310px w-266px">
                          {allCentersList?.length > 0 ? (
                            allCentersList.filter((item) =>
                              item.business_name
                                .toLowerCase()
                                .includes(searchTerm)
                            ).length > 0 ? (
                              allCentersList
                                .filter((item) =>
                                  item.business_name
                                    .toLowerCase()
                                    .includes(searchTerm)
                                )
                                .map((item: any, index: number) => (
                                  <div
                                    className="dropdown-item cursor-pointer text-break"
                                    key={index}
                                    onClick={() => handleRedirectCentre(item)}
                                  >
                                    <div
                                      className={`w-36px h-36px border-solid border-2 border-grey-dark radius-40px ${
                                        item && item?.photo !== null
                                          ? ""
                                          : "bg-white d-flex align-items-center justify-content-center"
                                      }`}
                                    >
                                      <img
                                        src={
                                          item?.photo
                                            ? item?.photo
                                            : DefaultCentreImg
                                        }
                                        alt="profile-picture"
                                        width={`${
                                          item?.photo !== null ? "32" : "24"
                                        }`}
                                        height={`${
                                          item?.photo !== null ? "32" : "24"
                                        }`}
                                        className="rounded-circle object-fit-cover"
                                      />
                                    </div>
                                    <div className="fs-16 f-400 text-black business-name">
                                      {item.business_name}
                                    </div>
                                  </div>
                                ))
                            ) : (
                              <div className="dropdown-item text-center text-grey-dark">
                                No centres found
                              </div>
                            )
                          ) : (
                            ""
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  allCentersList.map((item: any, index: number) => {
                    return (
                      <div
                        key={index}
                        className="d-flex gap-15 align-items-center"
                      >
                        <div
                          className={`w-44px h-44px border-solid border-2 border-grey-dark radius-40px ${
                            item && item?.photo !== null
                              ? ""
                              : "bg-white d-flex align-items-center justify-content-center"
                          }`}
                        >
                          <img
                            src={item?.photo ? item?.photo : DefaultCentreImg}
                            alt="profile-picture"
                            width={`${item?.photo !== null ? "40" : "24"}`}
                            height={`${item?.photo !== null ? "40" : "24"}`}
                            className="rounded-circle object-fit-cover"
                          />
                        </div>
                        <div className="d-none d-lg-flex flex-column gap-4">
                          <div
                            className="fs-22 fw-400 text-black lh-1"
                            title={`${item?.business_name}`}
                          >
                            {truncateString(item.business_name, 30)}
                          </div>
                          <div className="max-w-250px">
                            <div className="fs-13 fw-400 text-grey-dark">
                              {item?.address}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </>
            ) : null}
          </div>
        </div>

        <div className="page-inner-content d-flex flex-column">{children}</div>
      </div>

      {/* Show Popup if any pending timesheet approval for candidates */}
      {isCentreUsers && clientPendingTimesheetList.length > 0 && (
        <PendingTimesheetApproval
          clientPendingTimesheetList={clientPendingTimesheetList}
          setShowRatingPopup={setShowRatingPopup}
          setIsShowPendingTimesheetNotification={setIsShowPendingTimesheetNotification}
        />
      )}

      {/* Show Popup if any non rated candidates */}
      {isCentreUsers && nonRatedCandidates.length > 0 && (
        <PopupModal
          visible={!!showRatingPopup}
          showCloseBtn={true}
          onClickClose={handleClose}
          customWidth={650}
        >
          <RatingCandidate
            multipleCandidates={nonRatedCandidates}
            showConfirmBlock={(candidatesToBlock: any) =>
              showBlockConfirmationModal(candidatesToBlock)
            }
            onClose={handleClose}
          />
        </PopupModal>
      )}

      {/** Force candidate to fill bank details before submit the timesheet log */}
      {showBankAccountPopup && (
        <PopupModal
          visible={true}
          showCloseBtn={false}
          onClickClose={handleCloseBankAccPopup}
          customWidth={650}
        >
          <EditBankAccount handleClose={handleCloseBankAccPopup} type={"Add"} />
        </PopupModal>
      )}

      {/** Force candidate to log the timesheet */}
      {/* {(isCandidate && Object.values(candidatePendingTimesheetList).length > 0) && ( */}
      { showLogTimesheet && (
        <Logtimesheet
          candidatePendingTimesheetList={candidatePendingTimesheetList}
        />
      )}

      {showConfirmBlockPopup && (
        <ConfirmBlock
          showBlockModal={showConfirmBlockPopup}
          candidatesToBlock={blockList}
          onClose={hideBlockConfirmationModal}
        />
      )}
    </>
  );
};

export default memo(Portal);
